/**
 * ADB Message Event Emitter
 * 
 * Allows multiple handlers to subscribe to ADB messages from the WebRTC data channel.
 * This solves the problem where DeviceConnection only supports a single onAdbMessage callback.
 */

export type AdbMessageHandler = (data: ArrayBuffer) => void;

/**
 * ADB Message Emitter for multi-handler support
 * 
 * Since DeviceConnection.onAdbMessage only supports a single callback,
 * we use this emitter to multiplex ADB messages to multiple listeners.
 */
export class AdbMessageEmitter {
  private handlers = new Set<AdbMessageHandler>();
  private isAttached = false;

  /**
   * Subscribe to ADB messages
   * @param handler - Function to call when ADB message is received
   * @returns Unsubscribe function
   */
  subscribe(handler: AdbMessageHandler): () => void {
    this.handlers.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * Emit ADB message to all subscribed handlers
   * @param data - ADB message data
   */
  emit(data: ArrayBuffer): void {
    this.handlers.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error("[AdbMessageEmitter] Handler error:", error);
      }
    });
  }

  /**
   * Attach to device connection's onAdbMessage
   * Should only be called once per connection
   * @returns Function to detach the emitter
   */
  attach(onAdbMessage: (cb: (data: ArrayBuffer) => void) => void): () => void {
    if (this.isAttached) {
      console.warn("[AdbMessageEmitter] Already attached, detaching previous");
      this.detach();
    }

    onAdbMessage((data) => this.emit(data));
    this.isAttached = true;

    return () => this.detach();
  }

  /**
   * Detach from device connection
   */
  detach(): void {
    this.isAttached = false;
  }

  /**
   * Get number of active handlers
   */
  get handlerCount(): number {
    return this.handlers.size;
  }

  /**
   * Clear all handlers
   */
  clear(): void {
    this.handlers.clear();
    this.isAttached = false;
  }
}

/**
 * Global ADB message emitter singleton
 * Shared across all hooks that need ADB output
 */
let globalAdbEmitter: AdbMessageEmitter | null = null;

/**
 * Get or create the global ADB message emitter
 */
export function getAdbMessageEmitter(): AdbMessageEmitter {
  if (!globalAdbEmitter) {
    globalAdbEmitter = new AdbMessageEmitter();
  }
  return globalAdbEmitter;
}

/**
 * Reset the global emitter (useful for testing or reconnections)
 */
export function resetAdbMessageEmitter(): void {
  if (globalAdbEmitter) {
    globalAdbEmitter.clear();
  }
  globalAdbEmitter = null;
}
